import { Component, h } from "preact";
import Uploader from "../../uploader";
import {
  IEdges,
  ILabelOptionValue,
  IOption,
  IReactConfiguration,
} from "../../../typings";
import EventController from "../../../shared/controller/event-controller";
import OutputController from "../../../shared/controller/output-controller";
import UnitSystemController, {
  UnitSystem,
} from "../../../shared/controller/unit-system-controller";
import SizeController, {
  ISize,
} from "../../../shared/controller/size-controller";
import Cleave from "cleave.js";
import TemplateController from "../../../shared/controller/template-controller";
import LabelSvgSprite from "../../label-uploader/label-svg-sprite";
import LabelSizingUnitSystemComponent from "./LabelSizingUnitSystemComponent";

export default class LabelSizingComponent extends Component<
  ILabelSizingComponentProps,
  ILabelSizingComponentState
> {
  private elementWidth: any;
  private elementHeight: any;

  constructor(props: ILabelSizingComponentProps) {
    super(props);

    this.state = {
      sizeReference: props.conf.size_reference
        ? props.conf.size_reference
        : "us-quarter",
      sizeReferenceValue: 0,
      sizeImage: null,
      sizeReferenceOptions: [
        {
          text: props.option.meta.fields.usQuater,
          value: "us-quarter",
        },
        {
          text: props.option.meta.fields.euro20Cent,
          value: "eu-20-cent",
        },
      ],
      steps: 100,
    };

    this.onSlide = this.onSlide.bind(this);
    this.changeReference = this.changeReference.bind(this);
    this.loadEventListeners = this.loadEventListeners.bind(this);
    this.dispatchSizeReferenceValue =
      this.dispatchSizeReferenceValue.bind(this);
    this.dispatchSizeReferenceValueFocus =
      this.dispatchSizeReferenceValueFocus.bind(this);
    this.dispatchSizeReferenceValueBlur =
      this.dispatchSizeReferenceValueBlur.bind(this);
    this.setUnitSystem = this.setUnitSystem.bind(this);
  }

  componentDidMount(): void {
    this.loadEventListeners();
    const size: ISize = this.props.sizeController.getSize();
    const steps: number = this.props.sizeController.getSteps();

    const unitSystem: UnitSystem = this.props.outputController.getValue([
      "system",
      "unit-system",
    ]);

    this.setState({
      sizeImage: size,
      steps,
      unitSystem,
    });

    const edges: IEdges = this.props.outputController.getValue([
      "label",
      "edges",
    ]);
    if (edges) {
      this.setState({ edges });
    }

    const sizeReferenceValue: number = this.props.sizeController.getScale();
    if (sizeReferenceValue) {
      this.setState({ sizeReferenceValue });
    }
  }

  loadEventListeners(): void {
    let eventName: string = EventController.getSaveEventName(["size", "width"]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        const size: ISize = this.props.sizeController.getSize();
        const value: number = this.props.sizeController.getScale();
        const steps: number = this.props.sizeController.getSteps();
        this.setState({
          sizeImage: size,
          sizeReferenceValue: value,
          steps,
        });
      });

    eventName = EventController.getSaveEventName(["size", "height"]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        const size: ISize = this.props.sizeController.getSize();
        const value: number = this.props.sizeController.getScale();
        const steps: number = this.props.sizeController.getSteps();
        this.setState({
          sizeImage: size,
          sizeReferenceValue: value,
          steps,
        });
      });

    eventName = EventController.getSaveEventName(["system", "unit-system"]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        const unitSystem: UnitSystem = this.props.outputController.getValue([
          "system",
          "unit-system",
        ]);
        const steps: number = this.props.sizeController.getSteps();
        this.setState({
          unitSystem,
        });
      });

    eventName = EventController.getSaveEventName(["label", "size_reference"]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        const value: string = this.props.outputController.getValue([
          "label",
          "size_reference",
        ]);
        const steps: number = this.props.sizeController.getSteps();
        this.setState({
          sizeReference: value,
          steps,
        });
      });

    eventName = EventController.getSaveEventName(["label", "edges"]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        const edges: IEdges = this.props.outputController.getValue([
          "label",
          "edges",
        ]);
        if (edges) {
          const sizeImage: ISize = this.props.sizeController.getSize();
          const sizeReferenceValue: number =
            this.props.sizeController.getScale();
          const steps: number = this.props.sizeController.getSteps();
          this.setState({ sizeImage, sizeReferenceValue, edges, steps });
        }
      });
  }

  changeReference(e: any): void {
    const target: HTMLInputElement = e.target;
    const sizeReference: string = target.value;
    this.props.outputController
      .setValue(["label", "size_reference"], sizeReference)
      .then(() => {})
      .catch(() => {});
  }

  onSlide(event: any): void {
    let sizeReferenceValue: number = this.state.sizeReferenceValue;
    if (event !== null) {
      const target: HTMLInputElement = event.target as HTMLInputElement;
      const value: string = target.value;
      if (!value) {
        sizeReferenceValue = 0.0;
      } else {
        sizeReferenceValue = parseFloat(value) / this.state.steps;
      }
    }

    this.props.sizeController
      .setScale(sizeReferenceValue)
      .then(() => {})
      .catch(() => {});
  }

  private getTotalWidth(): number {
    return this.state.sizeImage.width;
  }

  private getTotalHeight(): number {
    return this.state.sizeImage.height;
  }

  private dispatchSizeReferenceValueFocus(): void {
    this.dispatchSizeReferenceValue("size-value_focus");
  }

  private dispatchSizeReferenceValueBlur(): void {
    this.dispatchSizeReferenceValue("size-value_blur");
  }

  private dispatchSizeReferenceValue(event: string): void {
    const dispatchEvent: CustomEvent = new CustomEvent(event);
    document.body
      .querySelector(this.props.conf.target)
      .dispatchEvent(dispatchEvent);
  }
  private switchUnitSystem(): void {
    if (this.props.unitSystemController.getUnitSystem() === UnitSystem.METRIC) {
      this.props.sizeController.setUnitSystem(UnitSystem.IMPERIAL);
    } else {
      this.props.sizeController.setUnitSystem(UnitSystem.METRIC);
    }
  }

  private setUnitSystem(unitSystem: UnitSystem): void {
    this.props.sizeController.setUnitSystem(unitSystem);
  }

  dimensionsLabel(cssClass: string = ""): JSX.Element {
    if (
      (!this.props.option.meta.fields.labelDimensions ||
        this.props.option.meta.fields.labelDimensions === "") &&
      (!this.props.option.meta.fields.adjustIndividualLabelDimensions ||
        this.props.option.meta.fields.adjustIndividualLabelDimensions === "")
    ) {
      return <span />;
    }

    return (
      <div className={cssClass}>
        <h5 className="mb-2 text-2xl font-bold text-black">
          {this.props.option.meta.fields.labelDimensions}
        </h5>
        <p className="text-xl text-gray-dark">
          {this.props.option.meta.fields.adjustIndividualLabelDimensions}
        </p>
      </div>
    );
  }

  render(): JSX.Element {
    const unitSystemOption: IOption =
      this.props.templateController.getOptionByCode("unit-system");
    const imperialValue: ILabelOptionValue =
      this.props.templateController.getOptionValue(
        unitSystemOption,
        "imperial",
      );
    const metricValue: ILabelOptionValue =
      this.props.templateController.getOptionValue(unitSystemOption, "metric");

    const showDimensions: boolean =
      this.props.option?.attributes?.fixed_steps?.value === undefined;

    return (
      <div>
        {showDimensions && (
          <div className="grid grid__column">
            {this.dimensionsLabel("grid__column--1-2")}
          </div>
        )}

        {showDimensions && this.state.sizeImage && (
          <div>
            <div className="flex justify-between my-8">
              <p className="text-xl">
                {unitSystemOption.meta.fields?.unitOfMeasurement}
              </p>
              <div>
                <button
                  type="button"
                  className={
                    "bg-gray-light relative inline-flex justify-start text-base font-base flex-shrink-0 h-10 w-24 p-0.5 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  }
                  role="switch"
                  onClick={() => this.switchUnitSystem()}
                  aria-checked="false"
                >
                  <div className="absolute flex w-full h-full text-gray-dark">
                    <span className="flex items-center justify-center w-1/2 h-9">
                      In
                    </span>
                    <span className="flex items-center justify-center w-1/2 h-9">
                      Cm
                    </span>
                  </div>
                  <span className="sr-only">Use setting</span>
                  <span
                    aria-hidden="true"
                    className={`${
                      this.props.unitSystemController.getUnitSystem() ===
                      UnitSystem.METRIC
                        ? "translate-x-12"
                        : "translate-x-0"
                    } pointer-events-none h-9 w-1/2 flex items-center justify-center rounded-full bg-blue text-white shadow transform ring-0 transition ease-in-out duration-200`}
                  >
                    {this.props.unitSystemController.getUnitSystem() ===
                    UnitSystem.METRIC
                      ? "Cm"
                      : "In"}
                  </span>
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-8 mb-8">
              <div>
                <p className="mb-2">
                  {this.props.option.meta.fields.widthInput}
                </p>
                <div className="relative flex overflow-hidden bg-white border border-solid rounded-md border-gray">
                  <input
                    className="border-none "
                    id=""
                    name=""
                    type="text"
                    placeholder={this.props.option.meta.fields.widthInput}
                    value={this.props.unitSystemController.convertUnitWithSystem(
                      this.getTotalWidth(),
                    )}
                    ref={(el: any | null) => {
                      if (!el || el === this.elementWidth) {
                        return;
                      }
                      this.elementWidth = el;
                      new Cleave(el, {
                        numeral: true,
                        numeralDecimalMark:
                          this.props.unitSystemController.getDecimal(),
                        delimiter: "",
                      });
                      el.value =
                        this.props.unitSystemController.convertUnitWithSystem(
                          this.getTotalWidth(),
                        );
                    }}
                    onChange={(event: Event) => {
                      const width: number = parseFloat(
                        (event.target as HTMLInputElement).value.replace(
                          ",",
                          ".",
                        ),
                      );
                      if (Number.isNaN(width)) {
                        return;
                      }
                      this.props.sizeController.setSize(
                        this.props.unitSystemController.getUnitSystem() ===
                          UnitSystem.METRIC
                          ? width * 10
                          : width * 25.4,
                        this.getTotalHeight(),
                      );
                    }}
                  />
                  <div className="absolute right-0 flex h-full">
                    <span
                      className="flex items-center px-3 text-black"
                      onClick={() =>
                        this.props.sizeController.setSize(
                          this.getTotalWidth() -
                            (this.props.unitSystemController.getUnitSystem() ===
                            UnitSystem.METRIC
                              ? 1
                              : 0.254),
                          this.getTotalHeight(),
                        )
                      }
                    >
                      <LabelSvgSprite
                        viewBox="0 0 32 32"
                        cssClass={"w-6 h-6"}
                        conf={this.props.conf}
                        imageCode="minus"
                        alt={"minus"}
                      />
                    </span>
                    <span
                      className="flex items-center px-3 text-black"
                      onClick={() =>
                        this.props.sizeController.setSize(
                          this.getTotalWidth() +
                            (this.props.unitSystemController.getUnitSystem() ===
                            UnitSystem.METRIC
                              ? 1
                              : 0.254),
                          this.getTotalHeight(),
                        )
                      }
                    >
                      <LabelSvgSprite
                        viewBox="0 0 32 32"
                        cssClass={"w-6 h-6"}
                        conf={this.props.conf}
                        imageCode="plus"
                        alt={"plus"}
                      />
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <p className="mb-2">
                  {this.props.option.meta.fields.heightInput}
                </p>
                <div className="relative flex overflow-hidden bg-white border border-solid rounded-md border-gray">
                  <input
                    className="border-none "
                    id=""
                    name=""
                    type="text"
                    placeholder={this.props.option.meta.fields.heightInput}
                    value={this.props.unitSystemController.convertUnitWithSystem(
                      this.getTotalHeight(),
                    )}
                    ref={(el: any | null) => {
                      if (!el || el === this.elementHeight) {
                        return;
                      }
                      this.elementHeight = el;
                      new Cleave(el, {
                        numeral: true,
                        numeralDecimalMark:
                          this.props.unitSystemController.getDecimal(),
                        delimiter: "",
                      });
                      el.value =
                        this.props.unitSystemController.convertUnitWithSystem(
                          this.getTotalHeight(),
                        );
                    }}
                    onChange={(event: Event) => {
                      const height: number = parseFloat(
                        (event.target as HTMLInputElement).value.replace(
                          ",",
                          ".",
                        ),
                      );
                      if (Number.isNaN(height)) {
                        return;
                      }
                      this.props.sizeController.setSize(
                        this.getTotalWidth(),
                        this.props.unitSystemController.getUnitSystem() ===
                          UnitSystem.METRIC
                          ? height * 10
                          : height * 25.4,
                      );
                    }}
                  />
                  <div className="absolute right-0 flex h-full">
                    <span
                      className="flex items-center px-3 text-black"
                      onClick={() =>
                        this.props.sizeController.setSize(
                          this.getTotalWidth(),
                          this.getTotalHeight() -
                            (this.props.unitSystemController.getUnitSystem() ===
                            UnitSystem.METRIC
                              ? 1
                              : 0.254),
                        )
                      }
                    >
                      <LabelSvgSprite
                        viewBox="0 0 32 32"
                        cssClass={"w-6 h-6"}
                        conf={this.props.conf}
                        imageCode="minus"
                        alt={"minus"}
                      />
                    </span>
                    <span
                      className="flex items-center px-3 text-black"
                      onClick={() =>
                        this.props.sizeController.setSize(
                          this.getTotalWidth(),
                          this.getTotalHeight() +
                            (this.props.unitSystemController.getUnitSystem() ===
                            UnitSystem.METRIC
                              ? 1
                              : 0.254),
                        )
                      }
                    >
                      <LabelSvgSprite
                        viewBox="0 0 32 32"
                        cssClass={"w-6 h-6"}
                        conf={this.props.conf}
                        imageCode="plus"
                        alt={"plus"}
                      />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={showDimensions ? " option__scale" : ""}>
          {!(showDimensions && this.state.sizeImage) && (
            <LabelSizingUnitSystemComponent
              imperialValue={imperialValue}
              metricValue={metricValue}
              setUnitSystem={this.setUnitSystem}
              unitSystem={this.state.unitSystem}
              unitSystemOption={unitSystemOption}
            >
              {this.dimensionsLabel()}
            </LabelSizingUnitSystemComponent>
          )}

          <div>
            <h5 className="mb-2 text-2xl font-bold text-black">
              {this.props.option.meta.fields.labelScale}
            </h5>
            <p className="text-xl text-gray-dark">
              {this.props.option.meta.fields.makeYourLabelBiggerOrSmaller}
            </p>

            <div className="mt-10 mb-6">
              <div className="relative flex bg-transparent border-0">
                <span className="absolute w-[calc(100%-1.55rem)] -m-px  h-px bg-gray top-6 left-3 right-3" />
                <input
                  type="range"
                  step={1}
                  min={0}
                  max={Math.round(this.state.steps)}
                  className={
                    "appearance-none bg-transparent w-full h-12 z-10 bg-gray-light rounded outline-none slider-thumb cursor-pointer relative"
                  }
                  list="ticks"
                  value={parseInt(
                    Math.round(
                      this.state.sizeReferenceValue *
                        Math.round(this.state.steps),
                    ) + "",
                  )}
                  onTouchStart={this.dispatchSizeReferenceValueFocus}
                  onTouchEnd={this.dispatchSizeReferenceValueBlur}
                  onMouseOver={this.dispatchSizeReferenceValueFocus}
                  onMouseLeave={this.dispatchSizeReferenceValueBlur}
                  onInput={this.onSlide}
                />
                {!showDimensions && (
                  <datalist
                    id="ticks"
                    className={
                      "flex overflow-hidden h-12 absolute left-0 right-0 z-0 justify-between px-3 py-0"
                    }
                  >
                    {[
                      ...Array(
                        showDimensions && this.state.sizeImage
                          ? 1
                          : this.state.steps + 1,
                      ).keys(),
                    ].map((value: any, key: number) => (
                      <option
                        key={key}
                        value="50"
                        className="relative z-0 justify-between block w-px h-12 p-0 bg-gray"
                      />
                    ))}
                  </datalist>
                )}
              </div>
            </div>

            {this.state.sizeImage && (
              <div className="mt-4 text-center">
                {this.props.unitSystemController.convertUnitWithSystem(
                  this.getTotalWidth(),
                )}{" "}
                {this.props.option.meta.fields.width}
                <br />
                {this.props.unitSystemController.convertUnitWithSystem(
                  this.getTotalHeight(),
                )}{" "}
                {this.props.option.meta.fields.height}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

interface ILabelSizingComponentProps {
  main: Uploader;
  conf: IReactConfiguration;
  option: IOption;

  eventController: EventController;
  outputController: OutputController;
  sizeController: SizeController;
  unitSystemController: UnitSystemController;
  templateController: TemplateController;
}

interface ILabelSizingComponentState {
  sizeImage: ISize;
  unitSystem: UnitSystem;
  sizeReference: string;
  sizeReferenceValue: number;
  sizeReferenceOptions: Array<ISelectOption>;
  steps: number;
  edges: IEdges;
}
